import type { FormFieldOption } from '../../types';
import { generateFieldId } from '../form-templates';
import countries from './data/countries.json';
import languages from './data/languages.json';
import currencies from './data/currencies.json';
import timezones from './data/timezones.json';
import industries from './data/industries.json';
import occupations from './data/occupations.json';

/** Stable identifiers shared by both wizard skills and the manual choice editor. */
export const CHOICE_LIBRARY_IDS = ['countries', 'languages', 'currencies', 'timezones', 'industries', 'occupations'] as const;
/** A maintained option source, independent of its current published version. */
export type ChoiceLibraryId = typeof CHOICE_LIBRARY_IDS[number];

/** A source snapshot with factual standard codes and publisher provenance. */
export interface ChoiceLibrary {
  readonly id: ChoiceLibraryId;
  readonly name: string;
  readonly standard: string;
  readonly version: string;
  readonly sourceUrl: string;
  readonly retrievedAt: string;
  readonly granularity: string;
  readonly license: string;
  readonly licenseUrl: string;
  readonly sourceSha256: string;
  readonly options: readonly Readonly<{ value: string; label: string }>[];
}

const sources = { countries, languages, currencies, timezones, industries, occupations };
const catalogues = CHOICE_LIBRARY_IDS.map(id => Object.freeze({
  ...sources[id],
  id,
  options: Object.freeze(sources[id].options.map(option => Object.freeze({ ...option }))),
} satisfies ChoiceLibrary));

/** Lean descriptors keep full catalogues out of model prompts. */
export const CHOICE_LIBRARIES = Object.freeze(catalogues.map(library => Object.freeze({
  id: library.id,
  name: library.name,
  standard: library.standard,
  version: library.version,
  optionCount: library.options.length,
})));

/** Read an immutable maintained snapshot; unknown references never fall back. */
export function getChoiceLibrary(id: string): ChoiceLibrary | undefined {
  return catalogues.find(library => library.id === id);
}

/** Copy a catalogue at insertion time so updates cannot change saved conversations. */
export function materializeChoiceLibrary(id: string): {
  options: FormFieldOption[];
  choiceLibrary: { id: ChoiceLibraryId; version: string };
} {
  const library = getChoiceLibrary(id);
  if (!library) throw new Error(`Unknown option library: ${id}`);
  return {
    options: library.options.map(option => ({ id: generateFieldId(), value: option.value, label: option.label })),
    choiceLibrary: { id: library.id, version: library.version },
  };
}
